import { Button, TextField } from '@ads/components-react';
import { CalendarDateRange, ChevronLeft, ChevronRight, Clock } from '@ads/icons';
import { useTranslation } from '@linka/i18n';
import { cn } from '@linka/utils';
import { format, isValid } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import type { DateRange as DayPickerRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { useMediaQuery } from '../../hooks/use-media-query';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';

type DateRange = {
  from?: string;
  to?: string;
};

export interface DateRangePickerI18n {
  placeholder: string;
  apply: string;
  cancel: string;
  /** Só renderizados no `mode='range'`, que expõe os campos de hora. */
  startTime?: string;
  endTime?: string;
}

type DateRangePickerProps = {
  /**
   * `'single'` seleciona um dia só e emite `{ from, to }` cobrindo o dia inteiro
   * (00:00 → 23:59), sem campos de hora e com botão de cancelar.
   */
  mode?: 'single' | 'range';
  /**
   * Copy já traduzida. Obrigatório em app sem o provider do `@linka/i18n`
   * (ex.: o participant, que usa next-intl) — sem isso o `t()` interno devolve a
   * própria chave. Omitir só onde o provider existe (organizer/backoffice).
   */
  i18n?: DateRangePickerI18n;
  /**
   * Container do portal do calendário. Por padrão o Radix portaliza no container do
   * gatilho — dentro de um dialog isso mantém o popover sob o lock de
   * `pointer-events` do modal, então o consumidor pode redirecionar se precisar.
   */
  portalContainer?: HTMLElement | null;
  /**
   * Dias selecionáveis. O que não casar fica desabilitado. Combine com
   * `fromDate`/`toDate` para também impedir navegar até meses/anos vazios —
   * `enabledDays` sozinho desabilita o dia, mas não bloqueia a navegação.
   */
  enabledDays?: Date[];
  /** Limite inferior de navegação e seleção (mês/ano anteriores ficam inalcançáveis). */
  fromDate?: Date;
  /** Limite superior de navegação e seleção. */
  toDate?: Date;
  /** Mês exibido ao abrir sem data selecionada. Default: mês corrente. */
  defaultMonth?: Date;
  date: DateRange;
  onDateChange: (date: DateRange) => void;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  id?: string;
  'aria-describedby'?: string;
  ref?: React.Ref<HTMLButtonElement>;
  portalTriggerClassName?: string;
};

function parseDateISO(iso?: string): { date?: Date; time: string } {
  if (!iso) return { time: '' };
  const d = new Date(iso);
  return isValid(d) ? { date: d, time: format(d, 'HH:mm') } : { time: '' };
}

function combineDateTime(date: Date, time: string, ms = 0): string {
  const [h = 0, m = 0, s = 0] = time.split(':').map(Number);
  const d = new Date(date);
  d.setHours(h, m, s, ms);
  return d.toISOString();
}

function combineEndOfDay(date: Date): string {
  return combineDateTime(date, '23:59:59', 999);
}

const dayPickerClassNames = {
  months: 'flex flex-col sm:flex-row',
  month: 'flex flex-col p-nano self-center',
  caption: 'flex justify-center relative items-center h-[32px] mb-quark',
  caption_label: 'ts-subtitle-xxs text-ctx-content-title capitalize',
  nav: 'flex items-center',
  nav_button: cn(
    'absolute flex items-center justify-center h-[28px] w-[28px]',
    'text-ctx-content-base',
    'disabled:pointer-events-none disabled:opacity-40',
  ),
  nav_button_previous: 'left-0',
  nav_button_next: 'right-0',
  table: 'w-full border-collapse',
  head_row: 'flex mb-quark',
  head_cell: 'w-[36px] text-center ts-caption-xxxs text-ctx-content-base',
  row: 'flex w-full mt-quark',
  cell: 'relative p-0 text-center',
  day: cn(
    'h-[36px] w-[36px] ts-paragraph-xs rounded-sm',
    'text-ctx-content-title hover:bg-base-primary-100',
    // Dia desabilitado (fora de `enabledDays`/`fromDate`/`toDate`) fica apagado e sem
    // feedback de hover — mesmo idioma do `nav_button` acima. Sem isto o dia bloqueado
    // é visualmente idêntico a um clicável e ainda destaca no hover.
    'disabled:pointer-events-none disabled:!text-base-gray-400 disabled:hover:bg-transparent',
  ),
  // O texto precisa do branco explícito: sobre o fundo roxo do dia selecionado, a cor
  // herdada (`text-ctx-content-title`) fica sem contraste. Mesmo token já usado pelos
  // extremos do range abaixo.
  day_selected: 'bg-ctx-content-button rounded-sm hover:bg-ctx-content-button !text-base-neutral-white',
  day_today: '',
  day_outside: '!text-base-gray-400 hover:!text-ctx-content-title',
  day_disabled: '!text-base-gray-400 pointer-events-none hover:!bg-transparent',
  day_range_start: 'rounded-r-none !text-base-neutral-white',
  day_range_end: 'rounded-l-none !text-base-neutral-white',
  day_range_middle: '!text-ctx-content-title !bg-base-primary-100 !rounded-none',
  day_hidden: '',
};

const TIME_FROM = '00:00';
const TIME_TO = '23:59';
const DATE_FORMAT = 'dd MMM yyyy HH:mm';
const SINGLE_DATE_FORMAT = 'dd/MM/yyyy';

const commonDayPickerProps = {
  locale: pt,
  showOutsideDays: true,
  components: {
    IconLeft: () => <ChevronLeft size={16} />,
    IconRight: () => <ChevronRight size={16} />,
  },
  classNames: dayPickerClassNames,
};

const toDayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

/** Matcher que desabilita todo dia fora de `enabledDays`. */
const buildDisabledMatcher = (enabledDays?: Date[]) => {
  if (!enabledDays) return undefined;
  const allowed = new Set(enabledDays.map(toDayKey));
  return (day: Date) => !allowed.has(toDayKey(day));
};

function resolveDisplayText(mode: 'single' | 'range', date: DateRange) {
  if (mode === 'single') return date.from ? format(new Date(date.from), SINGLE_DATE_FORMAT) : null;
  return date.from && date.to
    ? `${format(new Date(date.from), DATE_FORMAT)} → ${format(new Date(date.to), DATE_FORMAT)}`
    : null;
}

export const DateRangePicker = ({
  mode = 'range',
  i18n,
  portalContainer,
  enabledDays,
  fromDate,
  toDate,
  defaultMonth,
  date,
  onDateChange,
  disabled,
  id,
  portalTriggerClassName,
  ref,
  ...rest
}: DateRangePickerProps) => {
  const t = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isSingle = mode === 'single';
  const labels: DateRangePickerI18n = i18n ?? {
    placeholder: t(
      isSingle ? 'components.date-range-picker.placeholder-single' : 'components.date-range-picker.placeholder',
    ),
    apply: t('apply'),
    cancel: t('cancel'),
    startTime: t('start-time'),
    endTime: t('end-time'),
  };
  const [range, setRange] = useState<DayPickerRange | undefined>(() => ({
    from: parseDateISO(date.from).date,
    to: parseDateISO(date.to).date,
  }));
  const [timeFrom, setTimeFrom] = useState(() => parseDateISO(date.from).time || TIME_FROM);
  const [timeTo, setTimeTo] = useState(() => parseDateISO(date.to).time || TIME_TO);
  const [open, setOpen] = useState(false);

  // `fromDate`/`toDate` limitam navegação e seleção; `disabled` recorta os dias
  // dentro desse intervalo que não têm nada a oferecer.
  const boundsProps = {
    numberOfMonths: isSingle || !isDesktop ? 1 : 2,
    disabled: buildDisabledMatcher(enabledDays),
    fromDate,
    toDate,
    defaultMonth: defaultMonth ?? fromDate,
  };
  const displayText = resolveDisplayText(mode, date);
  const canApply = isSingle ? !!range?.from : !!range?.from && !!range?.to;

  const ariaInvalid = rest['aria-invalid'];
  const ariaDescribedby = rest['aria-describedby'];
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const from = parseDateISO(date.from);
      const to = parseDateISO(date.to);
      setRange({ from: from.date, to: to.date });
      setTimeFrom(from.time || TIME_FROM);
      setTimeTo(to.time || TIME_TO);
    }
    setOpen(next);
  };

  const handleApply = () => {
    if (!range?.from) return;
    if (isSingle) {
      onDateChange({
        from: combineDateTime(range.from, TIME_FROM),
        to: combineEndOfDay(range.from),
      });
      setOpen(false);
      return;
    }
    if (!range.to) return;
    onDateChange({
      from: combineDateTime(range.from, timeFrom),
      to: combineDateTime(range.to, timeTo),
    });
    setOpen(false);
  };

  useEffect(() => {
    const from = parseDateISO(date.from);
    const to = parseDateISO(date.to);
    setRange({ from: from.date, to: to.date });
    setTimeFrom(from.time || TIME_FROM);
    setTimeTo(to.time || TIME_TO);
  }, [date.from, date.to]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          id={id}
          type='button'
          hierarchy='tertiary'
          size='md'
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedby}
          leadingIcon={CalendarDateRange}
          className={cn(
            '!w-full !lg:w-fit border border-ctx-layout-border !text-ctx-content-base',
            portalTriggerClassName,
          )}
          custom={{
            root: cn('justify-center', {
              '!border-ctx-interactive-destructive': ariaInvalid,
            }),
          }}
        >
          <span
            className={cn('ts-caption-xxxs sm:ts-caption-xxs', {
              'text-ctx-content-placeholder': !displayText,
            })}
          >
            {displayText || labels.placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        container={portalContainer}
        className='w-(--radix-popover-trigger-width) sm:w-auto p-0'
        align='start'
      >
        {isSingle ? (
          <DayPicker
            mode='single'
            {...commonDayPickerProps}
            {...boundsProps}
            selected={range?.from}
            onSelect={(day) => setRange({ from: day, to: day })}
          />
        ) : (
          <DayPicker mode='range' {...commonDayPickerProps} {...boundsProps} selected={range} onSelect={setRange} />
        )}
        {!isSingle && (
          <div className='flex flex-row gap-nano border-t border-ctx-layout-border p-nano'>
            <TextField
              type='time'
              label={labels.startTime}
              value={timeFrom}
              onChange={(e) => setTimeFrom(e.currentTarget.value)}
              leadingIcon={Clock}
              size='sm'
              fullWidth
            />
            <TextField
              type='time'
              label={labels.endTime}
              value={timeTo}
              onChange={(e) => setTimeTo(e.currentTarget.value)}
              leadingIcon={Clock}
              size='sm'
              fullWidth
            />
          </div>
        )}
        <div className='flex flex-col gap-nano border-t border-ctx-layout-border p-nano'>
          <Button type='button' size='sm' fullWidth disabled={!canApply} onClick={handleApply}>
            {labels.apply}
          </Button>
          {isSingle && (
            <Button type='button' size='sm' hierarchy='tertiary' fullWidth onClick={() => handleOpenChange(false)}>
              {labels.cancel}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
